import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  getImpersonation,
  impersonationBannerHeight,
  stopImpersonation,
} from '../utils/impersonation'

// Un MASQUE, et surtout pas un triangle d'avertissement : rien ne va mal, on
// porte simplement le visage de quelqu'un d'autre.
function MaskIcon(props: React.ComponentProps<typeof Icon>) {
  return (
    <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path
        d="M3 8.5c0-1.4 1-2.5 2.4-2.5h13.2C20 6 21 7.1 21 8.5c0 4.6-2.2 8-5 8-1.6 0-2.7-1-3.4-2.4a.7.7 0 0 0-1.2 0C10.7 15.5 9.6 16.5 8 16.5c-2.8 0-5-3.4-5-8Z"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8.2" cy="10.6" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15.8" cy="10.6" r="1.1" fill="currentColor" stroke="none" />
    </Icon>
  )
}

// 🔑 Le cadrage de #98 a retiré, une par une, toutes les autres protections :
// on peut TOUT faire dans la peau de quelqu'un (Q1), la personne n'est PAS
// prévenue (Q3), et la session dure 30 jours comme les autres (Q6). Ce bandeau
// est donc la SEULE chose qui empêche d'écrire au nom d'un autre par
// distraction. Il ne se traite pas comme un détail d'interface.
export default function ImpersonationBanner() {
  const { t } = useTranslation()
  // ⚠️ Lu pendant le rendu, de façon synchrone : le bandeau ne réagit pas à un
  // changement du stockage, il se fige jusqu'au rechargement. C'est sans
  // conséquence tant que les DEUX transitions rechargent la page entière
  // (voir impersonation.ts) — le jour où l'une deviendrait une navigation, il
  // faudra rendre cet état réactif.
  const impersonation = getImpersonation()

  // 🎨 Un ocre brûlé, choisi parce qu'il n'appartient à personne d'autre dans
  // l'application : rouge = bloqué, jaune = en revue, bleu = actif, vert = clos
  // (voir theme.tsx). Emprunter l'une de ces couleurs ferait lire le bandeau
  // comme un état de tension ou de tâche.
  const bg = useColorModeValue('#A8410E', '#E9762F')
  const fg = useColorModeValue('#FFF6EF', '#1B1103')
  const stripe = useColorModeValue('#7A2E08', '#A8410E')

  // Hors impersonation, RIEN n'est rendu : pas de hauteur réservée, pas de
  // saut de mise en page.
  if (!impersonation) return null

  // La session de retour est perdue : l'entrée existe encore — on sait donc
  // qu'on incarne quelqu'un — mais elle ne porte plus de session exploitable.
  // Le bandeau change alors de discours, et son bouton déconnecte au lieu de
  // proposer un retour qui n'existe plus.
  //
  // ⚠️ Limite connue et assumée : si la clé disparaît ENTIÈREMENT, plus rien ne
  // permet de savoir qu'on incarne quelqu'un — la session nhost, elle, ne le
  // dit pas. Le bandeau ne s'affiche alors plus. Fermer ce dernier cas
  // demanderait que la session porte elle-même la marque de l'impersonation,
  // ce que le mécanisme du jeton ne fournit pas (voir #100).
  const isLost = !impersonation.adminSession

  const handleStop = () => stopImpersonation()

  return (
    <>
      {/* Le cadre hachuré, emprunté aux modes « test » des outils de paiement :
          il rend l'état impossible à oublier après dix minutes dans un écran.
          pointerEvents none, pour qu'il n'attrape jamais un clic destiné à
          l'application. */}
      <Box
        position="fixed"
        inset={0}
        zIndex={2000}
        pointerEvents="none"
        border="7px solid transparent"
        sx={{
          borderImage: `repeating-linear-gradient(45deg, ${bg} 0 9px, ${stripe} 9px 18px) 7`,
          '@media print': { display: 'none' },
        }}
        aria-hidden
      />

      {/* Un bloc de la pile des bandeaux (TopBanners, #77), qui est fixée en
          haut et décale la page de sa hauteur. */}
      <Flex
        h={`${impersonationBannerHeight}px`}
        align="center"
        gap={3}
        px={4}
        bg={bg}
        color={fg}
        fontWeight="medium"
        role="status"
        aria-live="polite"
      >
        <MaskIcon boxSize={5} flex="none" aria-hidden />

        {/* Le nom peut passer sur deux lignes sur petit écran ; le bouton, lui,
            ne part jamais. Une sortie qu'on doit chercher dans un menu est une
            sortie qu'on ne prend pas. */}
        <Flex direction="column" minW={0} flex="1">
          <Text fontSize="sm" opacity={0.85} lineHeight="short">
            {isLost
              ? t('ImpersonationBanner.lost')
              : t('ImpersonationBanner.label')}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" lineHeight="short">
            {impersonation.userName || t('ImpersonationBanner.unknownUser')}
          </Text>
        </Flex>

        <Button
          size="xs"
          flex="none"
          variant="outline"
          colorScheme="blackAlpha"
          color={fg}
          borderColor={fg}
          _hover={{ bg: 'blackAlpha.200' }}
          onClick={handleStop}
        >
          {isLost
            ? t('ImpersonationBanner.signOut')
            : t('ImpersonationBanner.stop')}
        </Button>
      </Flex>
    </>
  )
}
